"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { FormProvider } from "@/integrations/forms/FormProvider";
type FormState = {
  firstName: string;
  lastName: string;
  organization: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
};
const initialState: FormState = {
  firstName: "",
  lastName: "",
  organization: "",
  email: "",
  phone: "",
  inquiryType: "",
  message: ""
};
export default function ContactSection() {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<null | "success" | "error">(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...form,
          to: "admin@n2ncapital.pe"
        })
      });
      if (!res.ok) throw new Error("Failed to submit form");
      setStatus("success");
      setForm(initialState);
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="contact" className="relative overflow-hidden bg-[#FDFAF3] text-[#1C1917] pt-36 pb-24 md:pt-44">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.1] [background-image:linear-gradient(to_right,rgba(28,25,23,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(28,25,23,0.05)_1px,transparent_1px)] [background-size:48px_48px]" />

      {/* Gradient glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(180,83,9,0.03),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(180,83,9,0.02),transparent_28%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 xl:px-14">
        <div className="grid gap-16 lg:grid-cols-[0.95fr_1.05fr]">

          {/* LEFT SIDE */}
          <div className="max-w-[560px]">
            <div className="mb-10 flex items-center gap-4">
              <span className="h-px w-9 bg-[#B45309]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-[#B45309]">
                Get In Touch
              </span>
            </div>

            <h2 className="font-serif text-[3rem] md:text-[4.5rem] leading-[1.05] tracking-tight text-[#1C1917]">
              Ready to
              <br />
              <span className="italic text-[#B45309]">
                Invest Alongside Us?
              </span>
            </h2>

            <p className="mt-8 max-w-[520px] text-[1.1rem] leading-[1.8] text-[#1C1917]/70">
              Whether you are a limited partner, sovereign wealth fund, family
              office, co-investor, or a strategic partner — we welcome the
              conversation.
            </p>

            <div className="mt-14 space-y-10">
              <div>
                <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.34em] text-[#B45309]">
                  General & Investor Inquiries
                </div>
                <Link to="mailto:admin@n2ncapital.pe" className="text-[1.5rem] leading-snug text-[#1C1917] hover:text-[#B45309]">
                  admin@n2ncapital.pe
                </Link>
              </div>

              <div>
                <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.34em] text-[#B45309]">
                  Phone
                </div>
                <Link to="tel:+19256948999" className="text-[1.25rem] text-[#1C1917] hover:text-[#B45309]">
                  +1 925-694-8999
                </Link>
              </div>

              <div>
                <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.34em] text-[#B45309]">
                  Headquarters
                </div>
                <p className="text-[1.2rem] leading-[1.6] text-[#1C1917]">
                  3000 El Camino Real
                  <br />
                  Building 4, Suite 200
                  <br />
                  Palo Alto, CA 94301 · USA
                </p>
              </div>

              <div>
                <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.34em] text-[#B45309]">
                  Active Geographies
                </div>
                <p className="text-[1.2rem] text-[#1C1917]">
                  USA · Mexico · Asia
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE FORM */}
          <div>
            <div className="mb-10 flex items-center gap-4">
              <span className="h-px w-9 bg-[#B45309]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-[#B45309]">
                Investor Inquiry Form
              </span>
            </div>

            <FormProvider>
              <form onSubmit={handleSubmit} className="space-y-6">

                <div className="grid gap-5 md:grid-cols-2">
                  <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="First Name" required className="h-14 w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4" />
                  <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last Name" required className="h-14 w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4" />
                </div>

                <input name="organization" value={form.organization} onChange={handleChange} placeholder="Organization" className="h-14 w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4" />

                <div className="grid gap-5 md:grid-cols-2">
                  <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email" required className="h-14 w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4" />
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className="h-14 w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4" />
                </div>

                <select name="inquiryType" value={form.inquiryType} onChange={handleChange} required className="h-14 w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4">
                  <option value="">Select Inquiry Type</option>
                  <option>LP Investment</option>
                  <option>Co-Investment</option>
                  <option>Strategic Partnership</option>
                </select>

                <textarea name="message" value={form.message} onChange={handleChange} rows={5} placeholder="Message" required className="w-full border border-[#1C1917]/20 bg-[#F5EDD8] px-4 py-3" />

                <button className="h-14 bg-[#B45309] px-8 text-white font-mono text-xs uppercase tracking-widest">
                  {submitting ? "Submitting..." : "Submit Inquiry"}
                </button>

                {status === "success" && <p className="text-[#B45309]">Submitted successfully</p>}
                {status === "error" && <p className="text-red-500">Error submitting form</p>}

              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}